import { type ReactNode } from 'react';
import { getMarkdownElementComponents, type MarkdownVariant } from './markdownComponents';

/**
 * Asterisk-based bold/italic + backtick inline code. Underscore forms are skipped
 * so values like `click_through_rate` are not treated as emphasis.
 */
const INLINE_MARKDOWN_SPLIT_PATTERN = /(`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*)/;
export const stripInlineMarkdown = (text: string): string => text.replaceAll(/\*\*([^*]+)\*\*/g, '$1').replaceAll(/\*([^*]+)\*/g, '$1').replaceAll(/`([^`]+)`/g, '$1');

/**
 * Renders a limited subset of inline markdown (bold, italic, code) as React nodes.
 * Uses the same element components as the full Markdown renderer (`code` / `strong` / `em`).
 * Plain text without markers is returned unchanged.
 */
export const renderInlineMarkdown = (text: string, {
  variant = 'compact'
}: {
  variant?: MarkdownVariant;
} = {}): ReactNode => {
  if (!text.includes('*') && !text.includes('`')) {
    return text;
  }
  const parts = text.split(INLINE_MARKDOWN_SPLIT_PATTERN);
  if (parts.length === 1) {
    return text;
  }
  const {
    code: Code,
    strong: Strong,
    em: Em
  } = getMarkdownElementComponents(variant);
  return parts.map((part, index) => {
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      return <Code key={index} inline>
          {part.slice(1, -1)}
        </Code>;
    }
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <Strong key={index}>{part.slice(2, -2)}</Strong>;
    }
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      return <Em key={index}>{part.slice(1, -1)}</Em>;
    }
    return part.length > 0 ? <span key={index}>{part}</span> : null;
  });
};